import { Fragment, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import PageHeader from "../../components/PageHeader";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Building, Plus, Pencil, Trash2, Loader2, Search, Copy, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import CompagniaSmartField, { useCompPool } from "../../components/CompagniaSmartField";

const FIELDS = [
  { key: "denominazione", label: "Denominazione / Ragione sociale", w: 2 },
  { key: "tipo_ufficio", label: "Tipo ufficio", w: 2, type: "tipoufficio" },
  { key: "codice_ivass", label: "Codice ANIA" }, { key: "partita_iva", label: "Partita IVA" },
  { key: "codice_fiscale", label: "Codice Fiscale" }, { key: "referente", label: "Referente" },
  { key: "indirizzo", label: "Indirizzo", w: 2 }, { key: "numero_civico", label: "N° Civ." },
  { key: "comune", label: "Comune" }, { key: "provincia", label: "Provincia" }, { key: "cap", label: "CAP" },
  { key: "telefono", label: "Telefono" }, { key: "fax", label: "Fax" }, { key: "email", label: "Email" },
  { key: "pec", label: "PEC" }, { key: "sito_web", label: "Sito Web" }, { key: "note", label: "Note", w: 2 },
];
const CARD_OPTS = [
  { v: "aderisce", l: "Aderisce alla convenzione di risarcimento diretto" },
  { v: "non_aderisce", l: "Non aderisce alla convenzione di risarcimento diretto" },
];

function MasterDialog({ rec, onClose, onSaved, rows }) {
  const pool = useCompPool(rows);
  const [form, setForm] = useState({});
  const dups = useMemo(() => {
    const n = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]/g, "");
    const den = n(form.denominazione); const ania = n(form.codice_ivass);
    return (rows || []).filter((r) => r.id !== rec?.id).map((r) => ({
      r, den: den.length >= 3 && n(r.denominazione) === den, ania: !!ania && n(r.codice_ivass) === ania,
    })).filter((x) => x.den || x.ania);
  }, [rows, rec, form.denominazione, form.codice_ivass]);
  const [saving, setSaving] = useState(false);
  const [tuOpts, setTuOpts] = useState([]);
  useEffect(() => { setForm(rec && rec.id ? { ...rec } : { tipo_ufficio: "Sede Legale" }); }, [rec]);
  useEffect(() => { api.get("/admin/tipo-ufficio-options").then(({ data }) => setTuOpts(Array.isArray(data) ? data : [])).catch(() => setTuOpts([])); }, []);
  const save = async () => {
    if (!(form.denominazione || "").trim()) { toast.error("Inserisci la denominazione"); return; }
    setSaving(true);
    try {
      const payload = { ...Object.fromEntries(FIELDS.map((f) => [f.key, form[f.key] || ""])), convenzione_card: form.convenzione_card || "" };
      const { data } = rec.id ? await api.patch(`/admin/compagnie-master/${rec.id}`, payload) : await api.post("/admin/compagnie-master", payload);
      toast.success(rec.id ? "Compagnia aggiornata in tutti gli studi" : "Compagnia aggiunta a tutti gli studi");
      onSaved(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={!!rec} onOpenChange={(o) => { if (!o && !saving) onClose(); }}>
      <DialogContent className="bg-[#121824] border-slate-700 text-slate-100 max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="master-dialog">
        <DialogHeader><DialogTitle className="font-head">{rec?.id ? "Modifica compagnia" : "Nuova compagnia"}</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-3">
          {FIELDS.map((f) => (
            <Fragment key={f.key}>
            <div className={f.w === 2 ? "sm:col-span-2" : ""}>
              <Label className="text-slate-300 text-xs">{f.label}</Label>
              {f.type === "tipoufficio" ? (
                <Select value={form.tipo_ufficio || "nd"} onValueChange={(v) => setForm((x) => ({ ...x, tipo_ufficio: v === "nd" ? "" : v }))}>
                  <SelectTrigger className="mt-1 bg-slate-900/60 border-slate-700" data-testid="master-fld-tipo_ufficio"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="nd" data-testid="master-tu-opt-nd">Non specificato</SelectItem>
                    {[...tuOpts, ...(form.tipo_ufficio && !tuOpts.includes(form.tipo_ufficio) ? [form.tipo_ufficio] : [])].map((o) => (
                      <SelectItem key={o} value={o} data-testid={`master-tu-opt-${o}`}>{o}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : f.key === "note" ? (
                <Input value={form[f.key] || ""} onChange={(e) => setForm((x) => ({ ...x, [f.key]: e.target.value }))} data-testid={`master-fld-${f.key}`} className="mt-1 bg-slate-900/60 border-slate-700" />
              ) : (
                <CompagniaSmartField fkey={f.key} form={form} setForm={setForm} pool={pool} testId={`master-fld-${f.key}`} />
              )}
            </div>
            {f.key === "denominazione" && dups.length > 0 && (
              <div className="sm:col-span-2 rounded-md border border-amber-500/50 bg-amber-500/10 px-3 py-2 text-xs text-amber-200" data-testid="master-dup-warning" role="alert">
                <div className="flex items-center gap-1.5 font-semibold"><AlertTriangle className="h-3.5 w-3.5" />Possibile compagnia duplicata</div>
                <ul className="mt-1 space-y-0.5">
                  {dups.map(({ r, den, ania }) => (
                    <li key={r.id} data-testid={`master-dup-${r.id}`}>
                      <strong>{r.denominazione}</strong>{r.codice_ivass ? ` (Codice ANIA ${r.codice_ivass})` : ""} — coincide per {[den && "denominazione", ania && "Codice ANIA"].filter(Boolean).join(" e ")}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            </Fragment>
          ))}
          <div className="sm:col-span-2">
            <Label className="text-slate-300 text-xs">Convenzione risarcimento diretto</Label>
            <Select value={form.convenzione_card || "nd"} onValueChange={(v) => setForm((x) => ({ ...x, convenzione_card: v === "nd" ? "" : v }))}>
              <SelectTrigger className="mt-1 bg-slate-900/60 border-slate-700" data-testid="master-fld-convenzione_card"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="nd" data-testid="master-card-opt-nd">Non specificato</SelectItem>
                {CARD_OPTS.map((o) => <SelectItem key={o.v} value={o.v} data-testid={`master-card-opt-${o.v}`}>{o.l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={saving} data-testid="master-save" className="bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salva e distribuisci"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CopyFromStudio({ empty, onDone }) {
  const [tenants, setTenants] = useState([]);
  const [tid, setTid] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/admin/tenants").then(({ data }) => setTenants(data || [])).catch(() => {}); }, []);
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/compagnie-master/copia-da-studio", { tenant_id: tid });
      toast.success(`${data.added} compagnie copiate nella lista master (totale ${data.total})`);
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className={`pgp-card p-4 mb-4 flex flex-wrap items-center gap-2 ${empty ? "border-amber-500/50" : ""}`} data-testid="master-copy">
      <span className="text-sm text-slate-300 mr-1">Copia le compagnie di uno studio nella lista master:</span>
      <Select value={tid} onValueChange={setTid}>
        <SelectTrigger className="w-64 h-9" data-testid="master-copy-tenant"><SelectValue placeholder="Seleziona studio…" /></SelectTrigger>
        <SelectContent className="bg-[#121824] border-slate-700 text-slate-100">
          {tenants.map((t) => <SelectItem key={t.id} value={t.id}>{t.studio_name}</SelectItem>)}
        </SelectContent>
      </Select>
      <Button onClick={run} disabled={!tid || busy} data-testid="master-copy-run" variant="outline" className="h-9 border-slate-600 text-slate-200 hover:bg-slate-800">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Copy className="h-4 w-4 mr-1" /> Copia</>}
      </Button>
    </div>
  );
}

export default function CompagnieMaster() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [editRec, setEditRec] = useState(null);
  const load = () => {
    setLoading(true);
    api.get("/admin/compagnie-master").then(({ data }) => setRows(data || [])).catch((e) => toast.error(formatApiError(e.response?.data?.detail))).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return s ? rows.filter((r) => [r.denominazione, r.codice_ivass, r.comune].some((v) => String(v || "").toLowerCase().includes(s))) : rows;
  }, [rows, q]);
  const del = async (r) => {
    if (!window.confirm(`Eliminare "${r.denominazione}" dalla lista master? Verrà rimossa da tutti gli studi.`)) return;
    try { await api.delete(`/admin/compagnie-master/${r.id}`); setRows((rs) => rs.filter((x) => x.id !== r.id)); toast.success("Compagnia eliminata da tutti gli studi"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="compagnie-master-page">
      <PageHeader title="Compagnie Master" subtitle="Lista centralizzata delle compagnie assicurative, distribuita automaticamente a tutti gli studi." icon={Building}
        actions={<Button onClick={() => setEditRec({})} data-testid="master-new" className="bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold"><Plus className="h-4 w-4 mr-1" /> Nuova compagnia</Button>} />
      <CopyFromStudio empty={!loading && rows.length === 0} onDone={load} />
      <div className="pgp-card p-4">
        <div className="flex items-center gap-3 mb-3">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca…" data-testid="master-search" className="pl-9 w-64 h-9 bg-slate-900/60 border-slate-700" />
          </div>
          <span className="text-xs font-mono text-slate-400" data-testid="master-count">{rows.length} compagnie</span>
        </div>
        {loading ? <div className="py-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-amber-500" /></div> : !filtered.length ? (
          <p className="text-sm text-slate-500 py-6 text-center" data-testid="master-empty">Nessuna compagnia nella lista master.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="master-table">
              <thead><tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b border-slate-800">
                <th className="py-2 pr-3">Codice ANIA</th><th className="py-2 pr-3">Denominazione</th><th className="py-2 pr-3">Tipo ufficio</th><th className="py-2 pr-3">Comune</th><th className="py-2 pr-3">Contatti</th><th className="py-2" />
              </tr></thead>
              <tbody>{filtered.map((r) => (
                <tr key={r.id} className="border-b border-slate-800/70 hover:bg-slate-800/30" data-testid={`master-row-${r.id}`}>
                  <td className="py-2 pr-3 font-mono text-slate-300">{r.codice_ivass || "—"}</td>
                  <td className="py-2 pr-3 font-medium text-slate-100">{r.denominazione}</td>
                  <td className="py-2 pr-3 text-slate-300 whitespace-nowrap" data-testid={`master-tipo-${r.id}`}>{r.tipo_ufficio || "—"}</td>
                  <td className="py-2 pr-3 text-slate-300">{r.comune || "—"}{r.provincia ? ` (${r.provincia})` : ""}</td>
                  <td className="py-2 pr-3 text-xs text-slate-400">{[r.telefono, r.email, r.pec].filter(Boolean).join(" · ") || "—"}</td>
                  <td className="py-2 text-right whitespace-nowrap">
                    <Button size="sm" variant="ghost" onClick={() => setEditRec(r)} data-testid={`master-edit-${r.id}`} className="text-slate-400 hover:text-slate-100"><Pencil className="h-4 w-4" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => del(r)} data-testid={`master-del-${r.id}`} className="text-red-400 hover:text-red-300"><Trash2 className="h-4 w-4" /></Button>
                  </td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}
      </div>
      <MasterDialog rec={editRec} rows={rows} onClose={() => setEditRec(null)} onSaved={() => { setEditRec(null); load(); }} />
    </div>
  );
}
